import { useEffect, useState } from "react";

import { cn } from "./ui/cn";

interface CopyButtonProps {
  /** The text placed on the clipboard. */
  value: string;
  /** What the user sees; defaults to the value itself. */
  label?: string;
  /** Describes the thing being copied, e.g. "email". Used for the tooltip. */
  what?: string;
  className?: string;
}

/**
 * Click-to-copy text.
 *
 * Confirmation is inline and transient rather than a toast: agents copy an
 * address or a ticket number dozens of times an hour, and a stack of toasts for
 * an action that either obviously worked or obviously did not is noise.
 */
export function CopyButton({ value, label, what, className }: CopyButtonProps) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");

  // Reset after a beat, and cancel on unmount so a row scrolled out of the list
  // mid-timeout cannot set state on an unmounted component.
  useEffect(() => {
    if (state === "idle") return;
    const timer = setTimeout(() => setState("idle"), 1200);
    return () => clearTimeout(timer);
  }, [state]);

  async function copy(event: React.MouseEvent): Promise<void> {
    // Rows are clickable; copying must not also open the ticket.
    event.stopPropagation();
    event.preventDefault();

    try {
      await navigator.clipboard.writeText(value);
      setState("copied");
    } catch {
      // Clipboard access is denied in some embedded/insecure contexts. Say so
      // rather than silently doing nothing, so the agent knows to select by hand.
      setState("failed");
    }
  }

  return (
    <button
      type="button"
      onClick={copy}
      title={state === "failed" ? "Copy failed" : `Copy ${what ?? value}`}
      className={cn(
        /*
         * `relative` is load-bearing, and not for anything visible.
         *
         * The status line below is `sr-only`, which Tailwind implements as
         * `position: absolute` with no offsets. An absolutely positioned box is
         * clipped by an ancestor's `overflow` only when that ancestor is in its
         * containing block chain -- and without a positioned ancestor here, the
         * chain skipped every scroll container and landed somewhere near the
         * app frame. The span then sat at its static position *in document
         * coordinates*, outside the list's `overflow-auto`, and stretched the
         * document to wherever its row happened to fall.
         *
         * One per row, so a queue of a hundred tickets gave the page three
         * thousand pixels of scrollable nothing below the app: the window
         * scrolled the whole interface off the top and ended in blank space.
         * `overflow: hidden` on the shell does not fix it, because the span was
         * never being clipped by the shell in the first place.
         *
         * Positioning the button contains its own live region, which is where
         * the responsibility belongs -- a component that renders an absolutely
         * positioned child and leaves the containing block to chance leaks into
         * whatever scroll container it is dropped into.
         */
        "relative",
        "group/copy inline-flex max-w-full items-center gap-1 rounded px-1 -mx-1",
        "hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
        className,
      )}
    >
      <span className="truncate">{label ?? value}</span>
      <span
        aria-hidden
        className={cn(
          "shrink-0 text-[10px] leading-none transition-opacity",
          state === "idle"
            ? "opacity-0 group-hover/copy:opacity-60"
            : "opacity-100",
        )}
      >
        {state === "copied" ? "✓" : state === "failed" ? "✕" : "⧉"}
      </span>
      <span className="sr-only" role="status">
        {state === "copied"
          ? `Copied ${what ?? value}`
          : state === "failed"
            ? "Copy failed"
            : ""}
      </span>
    </button>
  );
}
